import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react"

import { LOCALES, detectLang, interpolate, persistLang, type Lang, type Params } from "./core"
import { de, en, type MessageKey } from "./messages"

export type { Lang, MessageKey }
export { LANGS } from "./core"

const dictionaries: Record<Lang, Record<MessageKey, string>> = { en, de }

// Module-level copy so non-React code (toasts in hooks, formatters) follows the same language.
let currentLang: Lang = detectLang()

export function getLang(): Lang {
  return currentLang
}

export function translate(key: MessageKey, params?: Params, lang: Lang = currentLang): string {
  return interpolate(dictionaries[lang][key] ?? en[key], params)
}

interface I18nValue {
  lang: Lang
  locale: string
  setLang: (lang: Lang) => void
  t: (key: MessageKey, params?: Params) => string
}

function buildValue(lang: Lang, setLang: (lang: Lang) => void): I18nValue {
  return { lang, locale: LOCALES[lang], setLang, t: (key, params) => translate(key, params, lang) }
}

// Without a provider (isolated component tests) the app behaves as English.
const I18nContext = createContext<I18nValue>(buildValue("en", () => {}))

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    currentLang = detectLang()
    return currentLang
  })

  const setLang = useCallback((next: Lang) => {
    currentLang = next
    persistLang(next)
    setLangState(next)
  }, [])

  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  const value = useMemo(() => buildValue(lang, setLang), [lang, setLang])
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nValue {
  return useContext(I18nContext)
}
